/**
 * SurfaceRulesModal — the official rules, from authored data.
 *
 * The platform-generic form of `MinelabRttrRulesModal`, which holds Race to
 * the Realm's rules as a hardcoded array in a brand-named file. That shape
 * has two costs: the one kind of content that changes on counsel's
 * instruction needs a deploy, and no second programme can have rules at all.
 * Here the sections are data (`SurfaceFaqConfig.rulesSections`).
 *
 * A MODAL rather than an external link, for the reason RTTR's own comment
 * records: an embedded WebView traps external navigation and exposes no
 * native bridge, so a hosted rules URL either fails to load or strands the
 * reader outside the surface.
 *
 * A fixed header over a scrolling body, so the close control stays reachable
 * through copy that runs to several thousand words.
 */
import React from "react";
import type {SurfaceRulesSection} from "@devour/client";
import "./SurfaceRulesModal.scss";

export interface SurfaceRulesModalProps {
    title: string;
    sections: readonly SurfaceRulesSection[];
    onClose: () => void;
}

export const SurfaceRulesModal: React.FC<SurfaceRulesModalProps> = ({title, sections, onClose}) => {
    const closeRef = React.useRef<HTMLButtonElement>(null);

    React.useEffect(() => {
        closeRef.current?.focus();
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    return (
        <div
            className="surface-rules"
            role="dialog"
            aria-modal="true"
            aria-label={title}
            onClick={e => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className="surface-rules__panel">
                <div className="surface-rules__head">
                    <h2 className="surface-rules__title">{title}</h2>
                    <button
                        ref={closeRef}
                        type="button"
                        className="surface-rules__close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>

                <div className="surface-rules__body">
                    {sections.map((section, i) =>
                        <section key={`rules-${i}`} className="surface-rules__section">
                            {/*
                              * A heading-less section is the lead disclaimer — the
                              * all-caps NO PURCHASE NECESSARY block that opens every
                              * set of rules. It carries no heading by design.
                              */}
                            {section.heading &&
                                <h3 className="surface-rules__heading">{section.heading}</h3>
                            }
                            {(section.paragraphs ?? []).map((p, j) =>
                                <p
                                    key={`rules-${i}-p${j}`}
                                    className={section.heading ? "surface-rules__p" : "surface-rules__p surface-rules__p--lead"}
                                >
                                    {p}
                                </p>)}
                        </section>)}
                </div>
            </div>
        </div>
    );
};

export default SurfaceRulesModal;
